import { useEffect } from 'react'
import {
  useDashboardFetchWaves,
  useReportDashboardFetchWave,
  waveFetchState,
  type DashboardFetchGate,
} from '@context/DashboardFetchWaveContext'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useHuddleEventsOverview } from '@hooks/dashboard/useHuddleEvents'
import { useHuddleLeaderboardOverview } from '@hooks/dashboard/useHuddleLeaderboard'
import { useHuddleProviderPerformanceOverview } from '@hooks/dashboard/useHuddleProviderPerformance'
import { useHuddleTodayForecastOverview } from '@hooks/dashboard/useHuddleTodayForecast'
import { useHuddleTomorrowForecastOverview } from '@hooks/dashboard/useHuddleTomorrowForecast'
import { useHuddleTomorrowScheduleAtAGlanceOverview } from '@hooks/dashboard/useHuddleTomorrowScheduleAtAGlance'
import { useHuddleTomorrowGoalsOverview } from '@hooks/dashboard/useHuddleTomorrowGoals'
import { useHuddleYesterdayActualsOverview } from '@hooks/dashboard/useHuddleYesterdayActuals'
import { useHuddleYesterdayPerformanceOverview } from '@hooks/dashboard/useHuddleYesterdayPerformance'
import { useHuddleYesterdayProviderActualsOverview } from '@hooks/dashboard/useHuddleYesterdayProviderActuals'
import { useHuddleYesterdayScheduleAdjustmentsOverview } from '@hooks/dashboard/useHuddleYesterdayScheduleAdjustments'
import { useHuddlePatientMetricsOverview } from '@hooks/dashboard/useHuddlePatientMetrics'
import { useHuddleYesterdaysPerformanceOverview } from '@hooks/dashboard/useHuddleYesterdaysPerformance'
import { HuddleDay } from '@constants/Dashboard/dashboard.enums'
import { createFailedToast } from '@utils/notificationToast.util'
import {
  buildHuddleSectionWaves,
  type HuddleSectionWaves,
} from './huddleSectionWaves'

export type HuddleDayTab = HuddleDay

export type { HuddleSectionWaves } from './huddleSectionWaves'

function huddleQueryDate(gate: DashboardFetchGate, date: string): string {
  return gate.enabled || gate.queued ? date : ''
}

type UseHuddleDashboardOptions = {
  activeDayTab: HuddleDayTab
  todayDate: string
  yesterdayDate: string
  tomorrowDate: string
  hiddenSections: Set<string>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred; exported hook is typed below
function useHuddleDashboardImpl({
  activeDayTab,
  todayDate,
  yesterdayDate,
  tomorrowDate,
  hiddenSections,
}: UseHuddleDashboardOptions) {
  const waves: HuddleSectionWaves = buildHuddleSectionWaves(
    activeDayTab,
    hiddenSections,
  )
  const isToday = activeDayTab === HuddleDay.Today
  const isYesterday = activeDayTab === HuddleDay.Yesterday
  const isTomorrow = activeDayTab === HuddleDay.Tomorrow
  const openWaves = useDashboardFetchWaves()
  const todayForecastGate = waveFetchState(openWaves, waves.todayForecast)
  const patientsGate = waveFetchState(openWaves, waves.patients)
  const providerPerformanceGate = waveFetchState(
    openWaves,
    waves.providerPerformance,
  )
  const yesterdaysPerformanceGate = waveFetchState(
    openWaves,
    waves.yesterdaysPerformance,
  )
  const leaderboardGate = waveFetchState(openWaves, waves.leaderboard)
  const eventsGate = waveFetchState(openWaves, waves.events)
  const yesterdayActualsGate = waveFetchState(openWaves, waves.yesterdayActuals)
  const yesterdayPerformanceGate = waveFetchState(
    openWaves,
    waves.yesterdayPerformance,
  )
  const yesterdayProviderActualsGate = waveFetchState(
    openWaves,
    waves.yesterdayProviderActuals,
  )
  const scheduleAdjustmentsGate = waveFetchState(
    openWaves,
    waves.scheduleAdjustments,
  )
  const tomorrowForecastGate = waveFetchState(openWaves, waves.tomorrowForecast)
  const tomorrowScheduleGate = waveFetchState(openWaves, waves.tomorrowSchedule)
  const tomorrowGoalsGate = waveFetchState(openWaves, waves.tomorrowGoals)

  // Empty date disables each huddle hook's query (see queryParams null guard).
  const patientsApi = useHuddlePatientMetricsOverview({
    enabled: patientsGate.enabled,
    queued: patientsGate.queued,
  })
  const todayForecastApi = useHuddleTodayForecastOverview({
    date: huddleQueryDate(todayForecastGate, todayDate),
    enabled: todayForecastGate.enabled,
    queued: todayForecastGate.queued,
  })
  const providerPerformanceApi = useHuddleProviderPerformanceOverview({
    date: huddleQueryDate(providerPerformanceGate, todayDate),
    enabled: providerPerformanceGate.enabled,
    queued: providerPerformanceGate.queued,
  })
  const yesterdaysPerformanceApi = useHuddleYesterdaysPerformanceOverview({
    date: huddleQueryDate(yesterdaysPerformanceGate, yesterdayDate),
    enabled: yesterdaysPerformanceGate.enabled,
    queued: yesterdaysPerformanceGate.queued,
  })
  const leaderboardApi = useHuddleLeaderboardOverview({
    date: huddleQueryDate(leaderboardGate, todayDate),
    enabled: leaderboardGate.enabled,
    queued: leaderboardGate.queued,
  })
  const eventsApi = useHuddleEventsOverview({
    date: huddleQueryDate(eventsGate, todayDate),
    enabled: eventsGate.enabled,
    queued: eventsGate.queued,
  })
  const yesterdayActualsApi = useHuddleYesterdayActualsOverview({
    date: huddleQueryDate(yesterdayActualsGate, yesterdayDate),
    enabled: yesterdayActualsGate.enabled,
    queued: yesterdayActualsGate.queued,
  })
  const yesterdayPerformanceApi = useHuddleYesterdayPerformanceOverview({
    date: huddleQueryDate(yesterdayPerformanceGate, yesterdayDate),
    enabled: yesterdayPerformanceGate.enabled,
    queued: yesterdayPerformanceGate.queued,
  })
  const yesterdayProviderActualsApi = useHuddleYesterdayProviderActualsOverview(
    {
      date: huddleQueryDate(yesterdayProviderActualsGate, yesterdayDate),
      enabled: yesterdayProviderActualsGate.enabled,
      queued: yesterdayProviderActualsGate.queued,
    },
  )
  const scheduleAdjustmentsApi = useHuddleYesterdayScheduleAdjustmentsOverview({
    date: huddleQueryDate(scheduleAdjustmentsGate, yesterdayDate),
    enabled: scheduleAdjustmentsGate.enabled,
    queued: scheduleAdjustmentsGate.queued,
  })
  const tomorrowForecastApi = useHuddleTomorrowForecastOverview({
    date: huddleQueryDate(tomorrowForecastGate, tomorrowDate),
    enabled: tomorrowForecastGate.enabled,
    queued: tomorrowForecastGate.queued,
  })
  const tomorrowScheduleAtAGlanceApi =
    useHuddleTomorrowScheduleAtAGlanceOverview({
      date: huddleQueryDate(tomorrowScheduleGate, tomorrowDate),
      enabled: tomorrowScheduleGate.enabled,
      queued: tomorrowScheduleGate.queued,
    })
  const tomorrowGoalsApi = useHuddleTomorrowGoalsOverview({
    date: huddleQueryDate(tomorrowGoalsGate, tomorrowDate),
    enabled: tomorrowGoalsGate.enabled,
    queued: tomorrowGoalsGate.queued,
  })

  useReportDashboardFetchWave(
    0,
    (waves.todayForecast === 0 && todayForecastApi.loading) ||
      (waves.patients === 0 && patientsApi.loading) ||
      (waves.yesterdayActuals === 0 && yesterdayActualsApi.loading) ||
      (waves.scheduleAdjustments === 0 && scheduleAdjustmentsApi.loading) ||
      (waves.tomorrowForecast === 0 && tomorrowForecastApi.loading) ||
      (waves.tomorrowSchedule === 0 && tomorrowScheduleAtAGlanceApi.loading),
  )
  useReportDashboardFetchWave(
    1,
    (waves.providerPerformance === 1 && providerPerformanceApi.loading) ||
      (waves.yesterdaysPerformance === 1 && yesterdaysPerformanceApi.loading) ||
      (waves.leaderboard === 1 && leaderboardApi.loading) ||
      (waves.events === 1 && eventsApi.loading) ||
      (waves.yesterdayPerformance === 1 && yesterdayPerformanceApi.loading) ||
      (waves.yesterdayProviderActuals === 1 &&
        yesterdayProviderActualsApi.loading) ||
      (waves.tomorrowGoals === 1 && tomorrowGoalsApi.loading),
  )

  const todayLoading =
    todayForecastApi.loading ||
    patientsApi.loading ||
    providerPerformanceApi.loading ||
    yesterdaysPerformanceApi.loading ||
    leaderboardApi.loading ||
    eventsApi.loading

  const yesterdayLoading =
    yesterdayActualsApi.loading ||
    yesterdayPerformanceApi.loading ||
    yesterdayProviderActualsApi.loading ||
    scheduleAdjustmentsApi.loading

  const tomorrowLoading =
    tomorrowForecastApi.loading ||
    tomorrowScheduleAtAGlanceApi.loading ||
    tomorrowGoalsApi.loading

  const todayHasError =
    (!todayForecastApi.loading && !!todayForecastApi.error) ||
    (!patientsApi.loading && !!patientsApi.error) ||
    (!providerPerformanceApi.loading && !!providerPerformanceApi.error) ||
    (!yesterdaysPerformanceApi.loading && !!yesterdaysPerformanceApi.error) ||
    (!leaderboardApi.loading && !!leaderboardApi.error) ||
    (!eventsApi.loading && !!eventsApi.error)

  const yesterdayHasError =
    (!yesterdayActualsApi.loading && !!yesterdayActualsApi.error) ||
    (!yesterdayPerformanceApi.loading && !!yesterdayPerformanceApi.error) ||
    (!yesterdayProviderActualsApi.loading &&
      !!yesterdayProviderActualsApi.error) ||
    (!scheduleAdjustmentsApi.loading && !!scheduleAdjustmentsApi.error)

  const tomorrowHasError =
    (!tomorrowForecastApi.loading && !!tomorrowForecastApi.error) ||
    (!tomorrowScheduleAtAGlanceApi.loading &&
      !!tomorrowScheduleAtAGlanceApi.error) ||
    (!tomorrowGoalsApi.loading && !!tomorrowGoalsApi.error)

  const loading = isToday
    ? todayLoading
    : isYesterday
      ? yesterdayLoading
      : tomorrowLoading

  const hasError = isToday
    ? todayHasError
    : isYesterday
      ? yesterdayHasError
      : tomorrowHasError

  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (!loading && hasError) {
      showToast(
        createFailedToast(
          'Failed to load metric',
          'Failed to load huddle dashboard data. Please try again.',
        ),
      )
    }
  }, [loading, hasError, showToast])

  return {
    loading,
    hasError,
    todayForecastApi,
    patientsApi,
    providerPerformanceApi,
    yesterdaysPerformanceApi,
    leaderboardApi,
    eventsApi,
    yesterdayActualsApi,
    yesterdayPerformanceApi,
    yesterdayProviderActualsApi,
    scheduleAdjustmentsApi,
    tomorrowForecastApi,
    tomorrowScheduleAtAGlanceApi,
    tomorrowGoalsApi,
  }
}

export type HuddleDashboard = ReturnType<typeof useHuddleDashboardImpl>

export function useHuddleDashboard(
  options: UseHuddleDashboardOptions,
): HuddleDashboard {
  return useHuddleDashboardImpl(options)
}
